/**
 * MythicReveal.jsx - Epic 5-phase reveal animation for mythic aircraft
 *
 * Phases:
 * 1. The Pull (1.5s) - Fade to black, rumble begins, screen shake
 * 2. The Tremor (1s) - Rumble climbs, silhouette appears
 * 3. The Tear (0.5s) - White flash, transition SFX
 * 4. The Reveal (2s) - Aircraft revealed with glow, particles, title
 * 5. Return to Reality - Triumphant, claim button appears
 * 6. Rewards - Show rewards granted ($3B, buffs)
 * 7. Form - Mythic design contribution form
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { soundManager, SOUNDS } from '../systems/SoundManager.js';
import { useWorldState } from './state/WorldStateContext.jsx';
import { uploadMythicRoll, isSteamLeaderboardAvailable } from '../lib/steam/leaderboards.js';
import { debug } from '../lib/debug.js';

// Mythic rewards constants
const MYTHIC_CASH_REWARD = 3_000_000_000; // $3 billion
const BUFF_DURATION_MS = 14 * 24 * 60 * 60 * 1000; // 14 days in real ms
const MYTHIC_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLScJ09yLg4LOsYvk6vNYbVGZe-lJbvH5TUbWXR_J7Oh2TRHz8w/viewform?usp=header";

// Phase durations in ms
const PHASE_DURATIONS = {
  PULL: 1500,
  TREMOR: 1000,
  TEAR: 500,
  REVEAL: 2000,
  REALITY: Infinity, // Until user clicks
};

// Particle/ember component
function Ember({ delay, duration }) {
  const style = {
    position: 'absolute',
    width: `${3 + Math.random() * 4}px`,
    height: `${3 + Math.random() * 4}px`,
    backgroundColor: Math.random() > 0.5 ? '#fbbf24' : '#f97316',
    borderRadius: '50%',
    left: `${20 + Math.random() * 60}%`,
    bottom: '20%',
    opacity: 0,
    filter: 'blur(1px)',
    boxShadow: '0 0 6px 2px rgba(251, 191, 36, 0.6)',
    animation: `ember-rise ${duration}ms ease-out ${delay}ms forwards`,
  };

  return <div style={style} />;
}

// Spark burst component
function Spark({ angle, distance }) {
  const rad = (angle * Math.PI) / 180;
  const x = Math.cos(rad) * distance;
  const y = Math.sin(rad) * distance;

  const style = {
    position: 'absolute',
    width: '4px',
    height: '4px',
    backgroundColor: '#fff',
    borderRadius: '50%',
    left: '50%',
    top: '45%',
    opacity: 0,
    boxShadow: '0 0 10px 4px rgba(255, 255, 255, 0.8)',
    animation: `spark-burst 0.6s ease-out forwards`,
    '--spark-x': `${x}px`,
    '--spark-y': `${y}px`,
  };

  return <div style={style} />;
}

export default function MythicReveal({ aircraft, onClaim, onClose }) {
  const { worldState, updateWorldState } = useWorldState();

  const [phase, setPhase] = useState(0); // 0=not started, 1-5=phases, 6=rewards, 7=form
  const [shakeIntensity, setShakeIntensity] = useState(0);
  const [rumbleVolume, setRumbleVolume] = useState(0);
  const [silhouetteOpacity, setSilhouetteOpacity] = useState(0);
  const [flashOpacity, setFlashOpacity] = useState(0);
  const [revealProgress, setRevealProgress] = useState(0);
  const [titleScale, setTitleScale] = useState(0.6);
  const [titleOpacity, setTitleOpacity] = useState(0);
  const [buttonOpacity, setButtonOpacity] = useState(0);
  const [embers, setEmbers] = useState([]);
  const [sparks, setSparks] = useState([]);
  const [rewardsApplied, setRewardsApplied] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const rumbleAudioRef = useRef(null);
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  // Start the sequence
  useEffect(() => {
    // Small delay before starting
    const startTimer = setTimeout(() => {
      setPhase(1);
    }, 300);

    return () => clearTimeout(startTimer);
  }, []);

  // Phase 1: The Pull
  useEffect(() => {
    if (phase !== 1) return;

    // Play rumble immediately
    debug('[MythicReveal] Phase 1 - Starting rumble');
    soundManager.play(SOUNDS.INTRO_MYTHIC_RUMBLE, { volume: 0.7 });

    // Animate rumble volume 0 -> 0.7
    let start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / PHASE_DURATIONS.PULL, 1);

      // Ramp volume up (visual only now, sound plays at fixed volume)
      const vol = progress * 0.7;
      setRumbleVolume(vol);
      setShakeIntensity(progress * 3);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setPhase(2);
      }
    };
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [phase]);

  // Phase 2: The Tremor
  useEffect(() => {
    if (phase !== 2) return;

    let start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / PHASE_DURATIONS.TREMOR, 1);

      // Rumble climbs 0.7 -> 1.0 (visual state only)
      const vol = 0.7 + progress * 0.3;
      setRumbleVolume(vol);
      // Shake intensifies
      setShakeIntensity(3 + progress * 5);
      // Silhouette fades in 0 -> 30%
      setSilhouetteOpacity(progress * 0.3);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setPhase(3);
      }
    };
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [phase]);

  // Phase 3: The Tear
  useEffect(() => {
    if (phase !== 3) return;

    // Stop rumble abruptly
    debug('[MythicReveal] Phase 3 - Playing whoosh NOW');
    setRumbleVolume(0);
    setShakeIntensity(0);

    // Play transition SFX immediately
    soundManager.play(SOUNDS.INTRO_WHOOSH, { volume: 1.0 });

    // White flash
    setFlashOpacity(1);

    // Generate sparks
    const newSparks = [];
    for (let i = 0; i < 24; i++) {
      newSparks.push({
        id: i,
        angle: i * 15,
        distance: 100 + Math.random() * 150,
      });
    }
    setSparks(newSparks);

    // Hold flash for 150ms then transition
    setTimeout(() => {
      setPhase(4);
    }, PHASE_DURATIONS.TEAR);

    return () => {};
  }, [phase]);

  // Phase 4: The Reveal
  useEffect(() => {
    if (phase !== 4) return;

    // Generate embers
    const newEmbers = [];
    for (let i = 0; i < 30; i++) {
      newEmbers.push({
        id: i,
        delay: Math.random() * 1500,
        duration: 2000 + Math.random() * 2000,
      });
    }
    setEmbers(newEmbers);

    let start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / PHASE_DURATIONS.REVEAL, 1);

      // Flash fades out
      setFlashOpacity(Math.max(0, 1 - progress * 2));

      // Reveal fades in
      setRevealProgress(progress);

      // Title animation: scale 0.6 -> 1.1 -> 1.0
      if (progress < 0.5) {
        setTitleScale(0.6 + progress * 1.0); // 0.6 -> 1.1
      } else {
        setTitleScale(1.1 - (progress - 0.5) * 0.2); // 1.1 -> 1.0
      }
      setTitleOpacity(Math.min(progress * 2, 1));

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setPhase(5);
      }
    };
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [phase]);

  // Phase 5: Return to Reality
  useEffect(() => {
    if (phase !== 5) return;

    // Fade in button
    let start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / 800, 1);
      setButtonOpacity(progress);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [phase]);

  // Screen shake effect
  const getShakeTransform = useCallback(() => {
    if (shakeIntensity === 0) return 'translate(0, 0)';
    const x = (Math.random() - 0.5) * shakeIntensity * 2;
    const y = (Math.random() - 0.5) * shakeIntensity * 2;
    return `translate(${x}px, ${y}px)`;
  }, [shakeIntensity]);

  // Update shake continuously when active
  useEffect(() => {
    if (shakeIntensity === 0) return;

    const shakeInterval = setInterval(() => {
      if (containerRef.current) {
        containerRef.current.style.transform = getShakeTransform();
      }
    }, 50);

    return () => clearInterval(shakeInterval);
  }, [shakeIntensity, getShakeTransform]);

  // Apply mythic rewards when claiming
  const applyMythicRewards = useCallback(() => {
    if (rewardsApplied) return;

    const now = Date.now();
    const expiresAt = now + BUFF_DURATION_MS;

    updateWorldState(prev => ({
      ...prev,
      airline: {
        ...prev.airline,
        cash: (prev.airline?.cash || 0) + MYTHIC_CASH_REWARD,
      },
      buffs: {
        ...prev.buffs,
        halfPriceFuel: {
          active: true,
          expiresAt: expiresAt,
          grantedAt: now,
        },
        doubleRevenue: {
          active: true,
          expiresAt: expiresAt,
          grantedAt: now,
        },
      },
      mythicRoll: {
        hasMythic: true,
        steamName: null, // TODO: Get from Steam API
        rolledAt: now,
      },
    }));

    setRewardsApplied(true);
    debug('[MythicReveal] Rewards applied: $3B cash, 14-day buffs activated');

    // Upload to Steam MythicRollers leaderboard
    if (isSteamLeaderboardAvailable()) {
      uploadMythicRoll().then(result => {
        if (result.success) {
          debug('[MythicReveal] Mythic roll uploaded to Steam leaderboard');
        } else {
          console.warn('[MythicReveal] Failed to upload to Steam leaderboard:', result.error);
        }
      });
    }
  }, [rewardsApplied, updateWorldState]);

  const handleClaim = () => {
    soundManager.play?.('SUCCESS', { volume: 0.8 });
    // Apply rewards and show rewards phase
    applyMythicRewards();
    setPhase(6); // Go to rewards phase
  };

  const handleContinueToForm = () => {
    setShowForm(true);
    setPhase(7); // Go to form phase
  };

  const handleOpenForm = () => {
    // Open the Google Form in a new tab
    window.open(MYTHIC_FORM_URL, '_blank', 'noopener,noreferrer');
  };

  const handleFinish = () => {
    onClaim?.(aircraft);
  };

  const aircraftImage = aircraft?.image || './aircraft/placeholder.png';
  const aircraftName = aircraft?.model || aircraft?.name || 'Unknown Aircraft';

  return (
    <>
      {/* CSS Keyframes */}
      <style>{`
        @keyframes ember-rise {
          0% {
            opacity: 0;
            transform: translateY(0) scale(1);
          }
          20% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translateY(-300px) translateX(${Math.random() * 100 - 50}px) scale(0.3);
          }
        }

        @keyframes spark-burst {
          0% {
            opacity: 1;
            transform: translate(0, 0) scale(1);
          }
          100% {
            opacity: 0;
            transform: translate(var(--spark-x), var(--spark-y)) scale(0.2);
          }
        }

        @keyframes glow-pulse {
          0%, 100% {
            filter: drop-shadow(0 0 30px rgba(251, 191, 36, 0.8)) drop-shadow(0 0 60px rgba(251, 191, 36, 0.4));
          }
          50% {
            filter: drop-shadow(0 0 50px rgba(251, 191, 36, 1)) drop-shadow(0 0 100px rgba(251, 191, 36, 0.6));
          }
        }

        @keyframes title-glow {
          0%, 100% {
            text-shadow: 0 0 20px rgba(251, 191, 36, 0.8), 0 0 40px rgba(251, 191, 36, 0.4);
          }
          50% {
            text-shadow: 0 0 30px rgba(251, 191, 36, 1), 0 0 60px rgba(251, 191, 36, 0.6);
          }
        }
      `}</style>

      {/* Main Container */}
      <div
        ref={containerRef}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Black overlay - fades in during Phase 1 */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: '#000',
            opacity: phase >= 1 ? 1 : 0,
            transition: phase === 1 ? 'opacity 0.5s ease-out' : 'none',
          }}
        />

        {/* White flash - Phase 3 */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: '#fff',
            opacity: flashOpacity,
            pointerEvents: 'none',
          }}
        />

        {/* Sparks - Phase 3 */}
        {sparks.map((spark) => (
          <Spark key={spark.id} angle={spark.angle} distance={spark.distance} />
        ))}

        {/* Silhouette - Phase 2-3, fades out during flash */}
        {phase >= 2 && phase <= 4 && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '400px',
              height: '250px',
              backgroundImage: `url(${aircraftImage})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              filter: 'brightness(0)',
              opacity: phase >= 4 ? 0 : silhouetteOpacity,
              transition: 'opacity 0.3s ease-out',
            }}
          />
        )}

        {/* Aircraft Reveal - Phase 4+ */}
        {phase >= 4 && (
          <div
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              opacity: revealProgress,
            }}
          >
            {/* Glow behind aircraft */}
            <div
              style={{
                position: 'absolute',
                width: '500px',
                height: '500px',
                background: 'radial-gradient(circle, rgba(251, 191, 36, 0.4) 0%, transparent 70%)',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                animation: 'glow-pulse 2s ease-in-out infinite',
              }}
            />

            {/* Aircraft Image */}
            <img
              src={aircraftImage}
              alt={aircraftName}
              style={{
                width: '400px',
                maxHeight: '250px',
                objectFit: 'contain',
                animation: phase >= 4 ? 'glow-pulse 2s ease-in-out infinite' : 'none',
                position: 'relative',
                zIndex: 1,
              }}
            />

            {/* Embers */}
            {embers.map((ember) => (
              <Ember key={ember.id} delay={ember.delay} duration={ember.duration} />
            ))}

            {/* Title */}
            <div
              style={{
                marginTop: '40px',
                textAlign: 'center',
                opacity: titleOpacity,
                transform: `scale(${titleScale})`,
                zIndex: 1,
              }}
            >
              <h1
                style={{
                  fontSize: '48px',
                  fontWeight: 'bold',
                  color: '#fbbf24',
                  margin: 0,
                  animation: phase >= 4 ? 'title-glow 2s ease-in-out infinite' : 'none',
                  letterSpacing: '4px',
                }}
              >
                {aircraftName.toUpperCase()}
              </h1>
              <p
                style={{
                  fontSize: '18px',
                  color: '#d4af37',
                  marginTop: '16px',
                  letterSpacing: '3px',
                  textTransform: 'uppercase',
                }}
              >
                MYTHIC AIRCRAFT DISCOVERED
              </p>
              <p
                style={{
                  fontSize: '14px',
                  color: '#a78bfa',
                  marginTop: '12px',
                  fontStyle: 'italic',
                }}
              >
                "There can only be one... and that one is you."
              </p>
            </div>

            {/* Claim Button - Phase 5 only */}
            {phase === 5 && (
              <button
                onClick={handleClaim}
                style={{
                  marginTop: '48px',
                  padding: '16px 64px',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#000',
                  backgroundColor: '#fbbf24',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  opacity: buttonOpacity,
                  transform: `scale(${0.9 + buttonOpacity * 0.1})`,
                  boxShadow: '0 0 30px rgba(251, 191, 36, 0.6)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  letterSpacing: '3px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 0 50px rgba(251, 191, 36, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 0 30px rgba(251, 191, 36, 0.6)';
                }}
              >
                CLAIM
              </button>
            )}
          </div>
        )}

        {/* Phase 6: Rewards Display */}
        {phase === 6 && (
          <div
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '40px',
              animation: 'fadeIn 0.5s ease-out',
            }}
          >
            <h2
              style={{
                fontSize: '36px',
                fontWeight: 'bold',
                color: '#fbbf24',
                marginBottom: '32px',
                letterSpacing: '4px',
                textShadow: '0 0 20px rgba(251, 191, 36, 0.8)',
              }}
            >
              MYTHIC REWARDS
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '40px' }}>
              {/* Cash Reward */}
              <div
                style={{
                  padding: '16px 32px',
                  backgroundColor: 'rgba(251, 191, 36, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(251, 191, 36, 0.3)',
                }}
              >
                <div style={{ color: '#fbbf24', fontSize: '24px', fontWeight: 'bold' }}>
                  +$3,000,000,000
                </div>
                <div style={{ color: '#888', fontSize: '14px' }}>Instant cash bonus</div>
              </div>

              {/* Half Price Fuel */}
              <div
                style={{
                  padding: '16px 32px',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                }}
              >
                <div style={{ color: '#3b82f6', fontSize: '24px', fontWeight: 'bold' }}>
                  50% Off Fuel
                </div>
                <div style={{ color: '#888', fontSize: '14px' }}>For 14 days</div>
              </div>

              {/* Double Revenue */}
              <div
                style={{
                  padding: '16px 32px',
                  backgroundColor: 'rgba(34, 197, 94, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                }}
              >
                <div style={{ color: '#22c55e', fontSize: '24px', fontWeight: 'bold' }}>
                  2x Revenue
                </div>
                <div style={{ color: '#888', fontSize: '14px' }}>For 14 days</div>
              </div>

              {/* Leaderboard */}
              <div
                style={{
                  padding: '16px 32px',
                  backgroundColor: 'rgba(168, 85, 247, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(168, 85, 247, 0.3)',
                }}
              >
                <div style={{ color: '#a855f7', fontSize: '24px', fontWeight: 'bold' }}>
                  Leaderboard Crown
                </div>
                <div style={{ color: '#888', fontSize: '14px' }}>For 30 days (coming soon)</div>
              </div>
            </div>

            <button
              onClick={handleContinueToForm}
              style={{
                padding: '16px 48px',
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#000',
                backgroundColor: '#fbbf24',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                boxShadow: '0 0 20px rgba(251, 191, 36, 0.4)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                letterSpacing: '2px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 0 30px rgba(251, 191, 36, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 0 20px rgba(251, 191, 36, 0.4)';
              }}
            >
              CONTINUE
            </button>
          </div>
        )}

        {/* Phase 7: Form */}
        {phase === 7 && (
          <div
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '40px',
              maxWidth: '500px',
              animation: 'fadeIn 0.5s ease-out',
            }}
          >
            <h2
              style={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: '#fbbf24',
                marginBottom: '16px',
                letterSpacing: '2px',
                textAlign: 'center',
              }}
            >
              HELP DESIGN THE NEXT MYTHIC
            </h2>

            <p
              style={{
                color: '#888',
                fontSize: '16px',
                textAlign: 'center',
                marginBottom: '32px',
                lineHeight: '1.6',
              }}
            >
              As a Mythic holder, you've earned the right to help shape the next legendary aircraft.
              Share your ideas with us.
            </p>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
              <button
                onClick={handleOpenForm}
                style={{
                  padding: '14px 32px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#000',
                  backgroundColor: '#fbbf24',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  boxShadow: '0 0 20px rgba(251, 191, 36, 0.4)',
                  transition: 'transform 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                OPEN FORM
              </button>

              <button
                onClick={handleFinish}
                style={{
                  padding: '14px 32px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#fbbf24',
                  backgroundColor: 'transparent',
                  border: '2px solid #fbbf24',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.backgroundColor = 'rgba(251, 191, 36, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                SKIP
              </button>
            </div>

            <p style={{ color: '#666', fontSize: '12px', textAlign: 'center' }}>
              Form opens in a new tab. You can always access it later from the EOS Store.
            </p>
          </div>
        )}
      </div>

      {/* Fade in animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
